import { FORTA_BLUE } from '../../../../../../utils/colors';

import type { ITreatmentPlanGoal } from '../../../../../../utils/types';

export const BRG_FOCUS_AREAS = ['behavior change'];
export const PCG_FOCUS_AREAS = ['parent training'];

export enum ThresholdTypes {
  Percent = 'percent',
  Number = 'number',
}

export enum GoalTypes {
  BehaviorReduction = 'behavior_reduction',
  SkillAcquisition = 'skill_acquisition',
  Caregiver = 'caregiver',
}

export enum MasteryCriteriaChangeTypeEnum {
  DECREASE = 'decrease',
  INCREASE = 'increase',
}

export enum DurationMethodMCMeasurementModeEnum {
  AVERAGE = 'average',
  TOTAL = 'total',
}

export enum GoalTypeOptions {
  All = 'all',
  BehaviorReduction = GoalTypes.BehaviorReduction,
  SkillAcquisition = GoalTypes.SkillAcquisition,
}

export enum HypothesizedFunctions {
  Sensory = 'Sensory',
  'Escape / Avoidance' = 'Escape / Avoidance',
  Attention = 'Attention',
  Tangible = 'Tangible',
}

export enum GoalMeasurementDurationUnit {
  Session = 'session',
  'Treatment Plan' = 'treatment plan',
  Day = 'day',
  Week = 'week',
  Month = 'month',
}

export enum TPGoalDataCollectionMethod {
  PERCENTAGE = 'PERCENTAGE',
  DURATION = 'DURATION',
  INSTANCES = 'INSTANCES',
  RATE = 'RATE',
}

export enum EventDurationMeasurementUnit {
  Second = 'SECOND',
  Minute = 'MINUTE',
  Hour = 'HOUR',
}

export enum RateMethodMeasurementUnitsIntervalUnitEnum {
  SECOND = 'seconds',
  MINUTE = 'minutes',
  HOUR = 'hours',
}

export enum DurationEventMeasurementUnitEnum {
  second = 'SECOND',
  minute = 'MINUTE',
  hour = 'HOUR',
}

export const DefaultMeasurementUnitValueV2Goal = {
  total_attempts: 10,
};

export const DefaultSAGMeasurementUnitValueV1Goal = {
  total_attempts: 10,
};

export const DefaultPCGMeasurementUnitValueV1Goal = {
  total_attempts: 4,
};

export const DefaultPercentageDCMValueV1Goal = (type: GoalTypes) => {
  switch (type) {
    case GoalTypes.SkillAcquisition:
      return {
        desired_amount: 8,
        practice_duration_count: 3,
        total_attempts: null,
      };
    case GoalTypes.Caregiver:
      return {
        desired_amount: 3,
        practice_duration_count: 2,
        total_attempts: null,
      };
  }
};

export const DefaultPercentageDCMValueV2Goal = (type: GoalTypes) => {
  switch (type) {
    case GoalTypes.SkillAcquisition:
      return {
        acquisition: {
          desired_amount: 8,
          practice_duration_count: 3,
          total_attempts: null,
        },
        generalization: {
          desired_amount: 8,
          practice_duration_count: 3,
          total_attempts: null,
        },
        maintenance: {
          desired_amount: 8,
          practice_duration_count: 10,
          total_attempts: null,
        },
        maintenance_frequency: null,
      };
    case GoalTypes.Caregiver:
      return {
        desired_amount: 8,
        practice_duration_count: 3,
      };
  }
};

export const DefaultPercentageDCMValueV2GoalV1Subresource = (type: GoalTypes) => {
  switch (type) {
    case GoalTypes.SkillAcquisition:
      return {
        acquisition: {
          desired_amount: null,
          practice_duration_count: null,
          total_attempts: null,
        },
        generalization: {
          desired_amount: null,
          practice_duration_count: null,
          total_attempts: null,
        },
        maintenance: {
          desired_amount: null,
          practice_duration_count: null,
          total_attempts: null,
        },
        maintenance_frequency: null,
      };
    case GoalTypes.Caregiver:
      return {
        desired_amount: null,
        practice_duration_count: null,
      };
  }
};

export const DefaultDurationMeasurementCriteria = {
  duration_unit: DurationEventMeasurementUnitEnum.minute,
};

export const DefaultRatesMeasurementCriteria = {
  interval_size: 1,
  interval_unit: RateMethodMeasurementUnitsIntervalUnitEnum.HOUR,
};

export const DefaultDurationMCValues = (
  type: GoalTypes,
  tpVersion: TreatmentPlanResourceVersion = TreatmentPlanResourceVersion.V2,
) => {
  switch (type) {
    case GoalTypes.BehaviorReduction:
      return {
        change_type: MasteryCriteriaChangeTypeEnum.DECREASE,
        desired_amount: null,
        practice_duration_count: 3,
        measurement_mode: DurationMethodMCMeasurementModeEnum.AVERAGE,
      };
    case GoalTypes.SkillAcquisition:
      return tpVersion === TreatmentPlanResourceVersion.V1
        ? {
            change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
            desired_amount: null,
            practice_duration_count: 3,
            measurement_mode: DurationMethodMCMeasurementModeEnum.AVERAGE,
          }
        : {
            acquisition: {
              change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
              desired_amount: null,
              practice_duration_count: 3,
              measurement_mode: DurationMethodMCMeasurementModeEnum.AVERAGE,
            },
            generalization: {
              change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
              desired_amount: null,
              practice_duration_count: 3,
              measurement_mode: DurationMethodMCMeasurementModeEnum.AVERAGE,
            },
            maintenance: {
              change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
              desired_amount: null,
              practice_duration_count: 10,
              measurement_mode: DurationMethodMCMeasurementModeEnum.AVERAGE,
            },
            maintenance_frequency: null,
          };
    case GoalTypes.Caregiver:
      return {
        change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: null,
        practice_duration_count: 3,
        measurement_mode: DurationMethodMCMeasurementModeEnum.AVERAGE,
      };
  }
};

export const DefaultRatesMCValues = (
  type: GoalTypes,
  tpVersion: TreatmentPlanResourceVersion = TreatmentPlanResourceVersion.V2,
) => {
  switch (type) {
    case GoalTypes.BehaviorReduction:
      return {
        change_type: MasteryCriteriaChangeTypeEnum.DECREASE,
        desired_amount: null,
        practice_duration_count: 3,
        total_attempts: null,
      };
    case GoalTypes.SkillAcquisition:
      return tpVersion === TreatmentPlanResourceVersion.V1
        ? {
            change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
            desired_amount: null,
            practice_duration_count: 3,
            total_attempts: null,
          }
        : {
            acquisition: {
              change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
              desired_amount: null,
              practice_duration_count: 3,
              total_attempts: null,
            },
            generalization: {
              change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
              desired_amount: null,
              practice_duration_count: 3,
              total_attempts: null,
            },
            maintenance: {
              change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
              desired_amount: null,
              practice_duration_count: 10,
              total_attempts: null,
            },
            maintenance_frequency: null,
          };
    case GoalTypes.Caregiver:
      return {
        change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: null,
        practice_duration_count: 3,
        total_attempts: null,
      };
  }
};

export const DefaultInstanceMCValues = (
  type: GoalTypes,
  tpVersion: TreatmentPlanResourceVersion = TreatmentPlanResourceVersion.V2,
) => {
  switch (type) {
    case GoalTypes.BehaviorReduction:
      return {
        change_type: MasteryCriteriaChangeTypeEnum.DECREASE,
        desired_amount: null,
        practice_duration_count: 3,
        practice_duration_unit: GoalMeasurementDurationUnit.Session,
      };
    case GoalTypes.Caregiver:
      return {
        change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: null,
        practice_duration_count: 3,
        practice_duration_unit: GoalMeasurementDurationUnit.Session,
      };
    case GoalTypes.SkillAcquisition:
      return tpVersion === TreatmentPlanResourceVersion.V1
        ? {
            change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
            desired_amount: null,
            practice_duration_count: 3,
            practice_duration_unit: GoalMeasurementDurationUnit.Session,
          }
        : {
            acquisition: {
              change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
              desired_amount: null,
              practice_duration_count: 3,
              practice_duration_unit: GoalMeasurementDurationUnit.Session,
            },
            generalization: {
              change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
              desired_amount: null,
              practice_duration_count: 3,
              practice_duration_unit: GoalMeasurementDurationUnit.Session,
            },
            maintenance: {
              change_type: MasteryCriteriaChangeTypeEnum.INCREASE,
              desired_amount: null,
              practice_duration_count: 3,
              practice_duration_unit: GoalMeasurementDurationUnit.Session,
            },
            maintenance_frequency: null,
          };
  }
};

export enum BaselineMeasurementVersion {
  V1 = 'v1',
  V2 = 'v2',
}

export const initialTreatmentPlanGoal: ITreatmentPlanGoal = {
  goal_type: GoalTypes.BehaviorReduction,
  is_maladaptive_correction: false,
  data_collection_method: null,
  status_at_creation: null,
  date_introduced: '',
  expected_mastery_date: null,
  baseline_at_creation: null,
  current_level_at_creation: null,
  mastery_criteria: null,
  resource_versions: {
    baseline_measurement: BaselineMeasurementVersion.V2,
  },
  targets: [],
  measurement_units: null,
  is_active: true,
  dirty: false,
  is_editing: true,
  expanded: true,
};

/** Field ids used for onUpdateField in goal details (GoalDetails, GoalsContainer). */
export const GOAL_DETAILS_FIELD_IDS = {
  GOAL_NAME_ID: 'goal_name_id',
  GOAL_NAME: 'goal_name',
  MALADAPTIVE_SYMPTOM_ID: 'maladaptive_symptom_id',
  IS_MALADAPTIVE_CORRECTION: 'is_maladaptive_correction',
  LINKED_SKILL_ACQUISITION_GOALS: 'linked_skill_acquisition_goals',
} as const;

export const GOAL_TARGET_NAME_MIN_CHARS = 2;
export const GOAL_TARGET_NAME_MAX_CHARS = 50;
export const REASON_FOR_DEACTIVATE_MAX_CHARS = 1000;

export const iconButtonStyle = {
  width: 64,
  height: 40,
  border: `2px solid ${FORTA_BLUE.primary}`,
  borderRadius: '20px',
  backgroundColor: 'transparent',
  mr: 3,

  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&:disabled': {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
};

export const BaselineUnitsOptions = [
  { value: 'Instance', label: 'Instances' },
  { value: 'Percentage', label: 'Percentage' },
];

export enum TargetStages {
  Baseline = 'Baseline',
  InProgress = 'In Progress',
  Acquisition = 'Acquisition',
  Generalization = 'Generalization',
  Maintenance = 'Maintenance',
  Mastered = 'Mastered',
  OnHold = 'On Hold',
  Resume = 'Resume',
  Discontinued = 'Discontinued',
}

export enum TargetStageStatus {
  Initial = 'initial',
  Progress = 'progress',
  Hold = 'hold',
  Resume = 'resume',
  Regress = 'regress',
  Discontinued = 'discontinued',
}

export enum TreatmentPlanResourceVersion {
  V1 = 'v1',
  V2 = 'v2',
}

export const STAGE_ORDER = {
  [GoalTypes.BehaviorReduction]: [
    TargetStages.Baseline,
    TargetStages.InProgress,
    TargetStages.Mastered,
  ],
  [GoalTypes.Caregiver]: [TargetStages.Baseline, TargetStages.InProgress, TargetStages.Mastered],
  [GoalTypes.SkillAcquisition]: [
    TargetStages.Baseline,
    TargetStages.Acquisition,
    TargetStages.Generalization,
    TargetStages.Maintenance,
    TargetStages.Mastered,
  ],
};
